import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FreeSwatches } from "../emails/new";
import { Package, Truck } from "lucide-react";
import { usePlacesWidget } from "react-google-autocomplete";
import { PayPalButton } from "react-paypal-button-v2";
import {
	resetSwatchCart,
	saveSwatchShippingAddress,
} from "../actions/cartActions";
import { createSwatchOrder } from "../actions/orderActions";
import {
	addEmailToCartMailChimp,
	apiMail,
	getUserDetails,
} from "../actions/userActions";
import { FormInput } from "../components/FormInput";
import { getTaxRate } from "../utils/getTaxRate";
import { useNavigate } from "react-router-dom";

const SwatchCheckout = () => {
	const dispatch = useDispatch();
	const { swatchCartItems } = useSelector((state) => state.swatchCart);

	const [shippingType, setShippingType] = useState("Standard");
	const [showPaypal, setShowPaypal] = useState(false);
	const [errors, setErrors] = useState({});
	const userInfo = useSelector((state) => state?.userLogin?.userInfo);
	const navigate = useNavigate();
	const isGuest = useSelector((state) => state?.userLogin?.isGuest);

	// Form States
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: userInfo?.email || "",
		phoneNumber: "",
		addressLine1: "",
		addressLine2: "",
		city: "",
		province: "",
		country: "Canada",
		postalCode: "",
		howDidYouHear: "",
	});

	// Google Places Integration
	const { ref } = usePlacesWidget({
		apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
		onPlaceSelected: (place) => {
			const addressComponents = place.address_components;
			const streetNumber =
				addressComponents.find((component) =>
					component.types.includes("street_number")
				)?.long_name || "";
			const street =
				addressComponents.find((component) => component.types.includes("route"))
					?.long_name || "";
			const city =
				addressComponents.find((component) =>
					component.types.includes("locality")
				)?.long_name || "";
			const province =
				addressComponents.find((component) =>
					component.types.includes("administrative_area_level_1")
				)?.short_name || "";
			const postalCode =
				addressComponents.find((component) =>
					component.types.includes("postal_code")
				)?.long_name || "";

			setFormData((prev) => ({
				...prev,
				addressLine1: `${streetNumber} ${street}`.trim(),
				city,
				province,
				postalCode,
			}));
		},
		options: {
			types: ["address"],
			componentRestrictions: {
				country: formData.country === "USA" ? "us" : "ca",
			},
		},
	});

	// Validation
	const validateForm = () => {
		const newErrors = {};

		// Phone validation
		const phoneRegex = /^\d{10}$/;
		if (!phoneRegex.test(formData.phoneNumber?.replace(/\D/g, ""))) {
			newErrors.phoneNumber = "Please enter a valid 10-digit phone number";
		}

		// Email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}

		// Required fields
		const requiredFields = [
			"firstName",
			"lastName",
			"addressLine1",
			"city",
			"province",
			"postalCode",
			"howDidYouHear",
		];
		requiredFields.forEach((field) => {
			if (!formData[field]) {
				newErrors[field] = "This field is required";
			}
		});

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		if (shippingType === "Express") {
			setShowPaypal(true);
			return;
		}

		handleOrder();
	};

	const successPaymentHandler = (paymentResult) => {
		if (paymentResult.status !== "COMPLETED") return;
		handleOrder();
	};

	const handleOrder = async () => {
		try {
			const tax = getTaxRate(formData.province);
			const price = shippingType === "Standard" ? 0 : 15 + tax * 15;

			// Save shipping address to Redux
			await dispatch(
				saveSwatchShippingAddress({
					...formData,
					price,
				})
			);

			// Create swatch order
			const insertedId = await dispatch(
				createSwatchOrder({
					swatchCartItems,
					swatchShippingAddress: {
						...formData,
						price,
					},
				})
			);

			// Prepare product list for email
			const productList = swatchCartItems.map((item) => ({
				name: `${item.item.ocs_name} - ${item.item.ocs_colour_name}`,
				img: encodeURI(
					`https://offcutshades.com/${item.item.image?.toLowerCase()}`
				),
			}));

			// Format full address for email
			const fullAddress = `${formData.addressLine1}${
				formData.addressLine2 ? ` - ${formData.addressLine2}` : ""
			}, ${formData.city}, ${formData.province}, ${formData.postalCode}, ${
				formData.country
			}`;

			// Send confirmation email
			await apiMail(
				formData.email,
				"Thank you for ordering your swatches!",
				<FreeSwatches
					orderNumber={insertedId}
					orderDate={new Date()}
					productList={productList}
					address={fullAddress}
					phoneNumber={formData.phoneNumber}
					shippingType={shippingType}
					name={`${formData.firstName} ${formData.lastName}`}
				/>
			);

			// Add email to MailChimp
			await addEmailToCartMailChimp(
				formData.email,
				"Swatches Ordered",
				formData.firstName,
				formData.lastName
			);

			// Reset cart and redirect
			await dispatch(resetSwatchCart());
			alert("Your order has been submitted successfully.");
			window.location.href = "/";
		} catch (error) {
			console.error("Order submission failed:", error);
			alert("There was a problem submitting your order. Please try again.");
		}
	};

	useEffect(() => {
		if (!isGuest) dispatch(getUserDetails(userInfo, navigate));
	}, [dispatch, userInfo, navigate]);

	const CartPreview = () => (
		<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
			<h3 className="font-medium mb-4">Cart Summary</h3>
			<div className="space-y-4">
				{swatchCartItems.map((item, index) => (
					<div key={index} className="flex items-center gap-3">
						<img
							src={item.item.image.toLowerCase()}
							alt={item.item.ocs_colour_name}
							className="w-12 h-12 rounded-xl object-cover"
						/>
						<div className="flex-1">
							<p className="font-medium text-sm">{item.item.ocs_name}</p>
							<p className="text-sm text-gray-500">
								{item.item.ocs_colour_name}
							</p>
						</div>
					</div>
				))}
			</div>
		</div>
	);

	const ShippingSelector = () => (
		<div className="space-y-4">
			<div
				className={`
          p-4 border-2 rounded-xl cursor-pointer transition-all
          ${
						shippingType === "Standard"
							? "border-primary bg-primary/5"
							: "border-gray-200 hover:border-primary/30"
					}
        `}
				onClick={() => setShippingType("Standard")}
			>
				<div className="flex items-center gap-4">
					<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
						<Package className="w-6 h-6 text-primary" />
					</div>
					<div>
						<p className="font-medium">Standard Shipping</p>
						<p className="text-sm text-gray-500">
							Free shipping with Canada Post (2-3 business days)
						</p>
					</div>
				</div>
			</div>

			<div
				className={`
          p-4 border-2 rounded-xl cursor-pointer transition-all
          ${
						shippingType === "Express"
							? "border-primary bg-primary/5"
							: "border-gray-200 hover:border-primary/30"
					}
        `}
				onClick={() => setShippingType("Express")}
			>
				<div className="flex items-center gap-4">
					<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
						<Truck className="w-6 h-6 text-primary" />
					</div>
					<div>
						<p className="font-medium">Express Shipping</p>
						<p className="text-sm text-gray-500">
							$15 + tax (Refundable with shade purchase)
						</p>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			<div className="max-w-6xl mx-auto px-4">
				<form onSubmit={handleSubmit} className="space-y-8">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						{/* Left Column - Form */}
						<div className="space-y-6">
							{/* Contact Information */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								<h3 className="font-medium mb-4">Contact Information</h3>
								<div className="space-y-4">
									<div className="grid grid-cols-2 gap-4">
										<div>
											<FormInput
												label="First Name"
												name="firstName"
												value={formData.firstName}
												onChange={handleInputChange}
												error={errors.firstName}
											/>
										</div>
										<div>
											<FormInput
												label="Last Name"
												name="lastName"
												value={formData.lastName}
												onChange={handleInputChange}
												error={errors.lastName}
											/>
										</div>
									</div>

									<div>
										<FormInput
											label="Email"
											type="email"
											name="email"
											value={formData.email}
											onChange={handleInputChange}
											error={errors.email}
										/>
									</div>

									<div>
										<FormInput
											label="Phone Number"
											type="tel"
											name="phoneNumber"
											value={formData.phoneNumber}
											onChange={handleInputChange}
											error={errors.phoneNumber}
										/>
									</div>
								</div>
							</div>

							{/* Shipping Address */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								<h3 className="font-medium mb-4">Shipping Address</h3>
								<div className="space-y-4">
									<div>
										<label className="block text-sm font-medium mb-1">
											Country
										</label>
										<select
											name="country"
											value={formData.country}
											onChange={handleInputChange}
											className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
										>
											<option value="Canada">Canada</option>
											<option value="USA">United States</option>
										</select>
									</div>

									<div>
										<FormInput
											label="Address"
											ref={ref}
											name="addressLine1"
											value={formData.addressLine1}
											onChange={handleInputChange}
											error={errors.addressLine1}
										/>
									</div>

									<div>
										<FormInput
											label="Apartment, suite, etc. (optional)"
											name="addressLine2"
											value={formData.addressLine2}
											onChange={handleInputChange}
										/>
									</div>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<FormInput
												label="City"
												name="city"
												value={formData.city}
												onChange={handleInputChange}
												error={errors.city}
											/>
										</div>
										<div>
											<FormInput
												label={
													formData.country === "USA" ? "State" : "Province"
												}
												name="province"
												maxLength={2}
												value={formData.province}
												onChange={handleInputChange}
												error={errors.province}
											/>
										</div>
									</div>

									<div>
										<FormInput
											label="Postal Code"
											name="postalCode"
											value={formData.postalCode}
											onChange={handleInputChange}
											error={errors.postalCode}
										/>
									</div>
								</div>
							</div>

							{/* Additional Information */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								<h3 className="font-medium mb-4">Additional Information</h3>
								<div>
									<label
										for="howDidYouHear"
										className="block text-sm font-medium mb-1"
									>
										How did you hear about us?
									</label>
									<select
										aria-label="How did you hear about us?"
										name="howDidYouHear"
										id="howDidYouHear"
										value={formData.howDidYouHear}
										onChange={handleInputChange}
										className={`
                      w-full px-3 py-2 rounded-xl border
                      ${
												errors.howDidYouHear
													? "border-red-500"
													: "border-gray-200"
											}
                      focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none
                    `}
									>
										<option value="">Select an option</option>
										<option value="Facebook">Facebook</option>
										<option value="Instagram">Instagram</option>
										<option value="Social Media">Social Media</option>
										<option value="Search Engine">Search Engine</option>
										<option value="Advertisement">Advertisement</option>
										<option value="Word of mouth">Word of mouth</option>
										<option value="Other">Other</option>
									</select>
									{errors.howDidYouHear && (
										<p className="text-red-500 text-xs mt-1">
											{errors.howDidYouHear}
										</p>
									)}
								</div>
							</div>
						</div>

						{/* Right Column - Order Summary & Shipping */}
						<div className="space-y-6">
							{/* Cart Preview */}
							<CartPreview />

							{/* Shipping Options */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								<h3 className="font-medium mb-4">Shipping Method</h3>
								<ShippingSelector />
							</div>

							{/* Order Summary */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								<h3 className="font-medium mb-4">Order Summary</h3>
								<div className="space-y-2">
									<div className="flex justify-between">
										<span>Samples ({swatchCartItems.length})</span>
										<span>Free</span>
									</div>
									<div className="flex justify-between">
										<span>Shipping</span>
										<span>
											{shippingType === "Standard" ? "Free" : "$15.00"}
										</span>
									</div>
									{shippingType === "Express" && (
										<div className="flex justify-between">
											<span>Tax</span>
											<span>
												${(15 * getTaxRate(formData.province)).toFixed(2)}
											</span>
										</div>
									)}
									<div className="border-t border-gray-200 mt-4 pt-4">
										<div className="flex justify-between font-medium">
											<span>Total</span>
											<span>
												{shippingType === "Standard"
													? "Free"
													: `$${(
															15 +
															15 * getTaxRate(formData.province)
													  ).toFixed(2)}`}
											</span>
										</div>
									</div>
								</div>
							</div>

							{/* Complete Order Button/PayPal */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								{showPaypal ? (
									<PayPalButton
										options={{
											components: "buttons",
											currency: formData.country === "USA" ? "USD" : "CAD",
											clientId:
												process.env.NODE_ENV === "production"
													? process.env.REACT_APP_PRODUCTION_PAYPAL_ID
													: process.env.REACT_APP_SANDBOX_PAYPAL_ID,
										}}
										amount={(15 + 15 * getTaxRate(formData.province)).toFixed(
											2
										)}
										onSuccess={successPaymentHandler}
										style={{
											shape: "pill",
											layout: "vertical",
										}}
									/>
								) : (
									<button
										type="submit"
										className="w-full px-6 py-3 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-colors"
									>
										{shippingType === "Standard"
											? "Complete Order"
											: "Proceed to Payment"}
									</button>
								)}
							</div>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default SwatchCheckout;
